import {
    ArrowBack,
    AttachFile,
    Business,
    Check,
    ContentCopy,
    MoreVert,
    Description,
    DrawRounded,
    Extension,
    Loyalty,
    OpenInNew,
    Palette,
    Payments,
    ShareRounded,
    SwapHoriz,
    Upgrade,
} from '@mui/icons-material';
import {
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { LoadingContent } from '../../../components/Loading';
import useErrors from '../../../hooks/useErrors';
import { useFetch } from '../../../hooks/useFecth';
import { useAuth } from '../../../context/AuthContext';
import { APP_BASE } from '../../../config/env';
import Dropdown from '../../../components/Dropdown';
import server from '../../../services/server';
import {
    AdditionalProducts,
    ApplyUpgrade,
    Contract,
    ContractFiles,
    ContractTextPreview,
    Customizacao,
    Fidelidade,
    Migration,
    Pagamento,
    ProposalGates,
    ProposalSectionCard,
    Termo,
} from './components';

export default function ProposalPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { enqueueSnackbar, setErrors } = useErrors();
    const confirm = useConfirm();
    const { data, mutate, isFetching, error } = useFetch(`subscription/${id}`);
    const [linkCopied, setLinkCopied] = useState(false);
    const isSuper = Boolean(user?.super);

    if (isFetching) return <LoadingContent />;
    if (error) {
        return (
            <Container maxWidth="xl">
                <Typography color="error">Erro ao carregar proposta: {error?.message}</Typography>
            </Container>
        );
    }
    if (!data) {
        return (
            <Container maxWidth="xl">
                <Typography>Proposta não encontrada.</Typography>
            </Container>
        );
    }

    const proposalTitle =
        data?.Club?.name ||
        data?.contractData?.contract?.companyName ||
        data?.contractData?.contract?.tradeName ||
        data?.contractData?.contract?.contractorName ||
        'Proposta de adesão';

    const shareUrl = `${APP_BASE}/assinatura/${data?.uuid}`;

    async function handleCancelProposal() {
        try {
            await confirm({
                title: 'Cancelar proposta',
                description: 'Tem certeza que deseja cancelar esta proposta? Ela irá para a aba de canceladas.',
                confirmationText: 'Cancelar proposta',
                cancellationText: 'Voltar',
            });
            await server.delete(`subscription/${data.uuid}`);
            enqueueSnackbar('Proposta cancelada com sucesso.', { variant: 'success' });
            navigate('/dashboard/propostas?isCancelled=true');
        } catch (error) {
            if (error !== undefined) setErrors(error);
        }
    }

    async function handlePermanentDelete() {
        try {
            await confirm({
                title: 'Excluir proposta permanentemente',
                description:
                    'Tem certeza de que deseja excluir esta proposta permanentemente? Pedidos, assinatura e arquivos da proposta serão apagados. Esta ação não pode ser desfeita.',
                confirmationText: 'Excluir permanentemente',
                cancellationText: 'Voltar',
            });
            await server.delete(`subscription/${data.uuid}`, { params: { permanent: true } });
            enqueueSnackbar('Proposta excluída permanentemente.', { variant: 'success' });
            navigate('/dashboard/propostas');
        } catch (error) {
            if (error !== undefined) setErrors(error);
        }
    }

    async function handleCopyShareUrl() {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setLinkCopied(true);
            enqueueSnackbar('Link copiado com sucesso!', { variant: 'success' });
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            enqueueSnackbar('Não foi possível copiar o link.', { variant: 'error' });
        }
    }

    return (
        <Container maxWidth="xl">
            <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton component={Link} to="/dashboard/propostas" aria-label="Voltar">
                        <ArrowBack />
                    </IconButton>
                    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h4" component="h1" noWrap>
                                {proposalTitle}
                            </Typography>
                            <Chip label={`#${data.id}`} size="small" />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Proposta de contrato de adesão
                        </Typography>
                    </Stack>
                    {isSuper && (
                        <Dropdown>
                            <IconButton aria-label="Mais ações">
                                <MoreVert />
                            </IconButton>
                            <Menu>
                                <MenuItem onClick={handleCancelProposal}>Cancelar proposta</MenuItem>
                                <MenuItem onClick={handlePermanentDelete} sx={{ color: 'error.main' }}>
                                    Excluir permanentemente
                                </MenuItem>
                            </Menu>
                        </Dropdown>
                    )}
                </Stack>

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <ShareRounded color="primary" sx={{ mt: 0.25 }} />
                            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2">Link de assinatura</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Envie este link para o contratante concluir a adesão ao clube.
                                </Typography>
                            </Stack>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={shareUrl}
                                InputProps={{
                                    readOnly: true,
                                    sx: { fontFamily: 'monospace', fontSize: 13 },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={linkCopied ? 'Copiado!' : 'Copiar link'}>
                                                <IconButton onClick={handleCopyShareUrl} edge="end" aria-label="Copiar link">
                                                    {linkCopied ? <Check color="success" fontSize="small" /> : <ContentCopy fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNew />}
                                component="a"
                                href={shareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                            >
                                Abrir link
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <ProposalGates contract={data} mutate={mutate} />
                </Paper>

                <Divider />

                <ProposalSectionCard title="Dados do contratante" icon={Business}>
                    <Termo contract={data} mutate={mutate} />
                </ProposalSectionCard>

                <ProposalSectionCard title="Plano e mensalidade" icon={Payments}>
                    <Pagamento contract={data} mutate={mutate} />
                </ProposalSectionCard>

                <ProposalSectionCard title="Fidelidade do contrato" icon={Loyalty}>
                    <Fidelidade contract={data} mutate={mutate} />
                </ProposalSectionCard>

                <ProposalSectionCard title="Customização" icon={Palette}>
                    <Customizacao contract={data} mutate={mutate} />
                </ProposalSectionCard>

                <ProposalSectionCard title="Benefícios adicionais" icon={Extension}>
                    <AdditionalProducts contract={data} mutate={mutate} />
                </ProposalSectionCard>

                <ProposalSectionCard title="Texto do contrato" icon={Description}>
                    <ContractTextPreview contract={data} mutate={mutate} />
                </ProposalSectionCard>

                <ProposalSectionCard title="Assinatura do contrato" icon={DrawRounded}>
                    <Contract contract={data} mutate={mutate} />
                </ProposalSectionCard>

                {Boolean(data?.clubId) && (
                    <ProposalSectionCard title="Aplicar upgrade no clube" icon={Upgrade}>
                        <ApplyUpgrade contract={data} mutate={mutate} />
                    </ProposalSectionCard>
                )}

                <ProposalSectionCard title="Arquivos do contrato" icon={AttachFile}>
                    <ContractFiles contract={data} mutate={mutate} />
                </ProposalSectionCard>

                {user?.super && (
                    <ProposalSectionCard title="Migração de clube" icon={SwapHoriz}>
                        <Migration contract={data} mutate={mutate} />
                    </ProposalSectionCard>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                    <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            Criada em: {new Date(data.createdAt).toLocaleString('pt-BR')}
                        </Typography>
                        {data.updatedAt && (
                            <Typography variant="caption" color="text.secondary">
                                Última alteração: {new Date(data.updatedAt).toLocaleString('pt-BR')}
                            </Typography>
                        )}
                        {data.contractExpiredAt && (
                            <Typography variant="caption" color="text.secondary">
                                Contrato expira em: {new Date(data.contractExpiredAt).toLocaleString('pt-BR')}
                            </Typography>
                        )}
                    </Stack>
                    <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate('/dashboard/propostas')}
                    >
                        Voltar para lista
                    </Typography>
                </Stack>
            </Stack>
        </Container>
    );
}
